import {
  ontologyCreateNamespace,
  ontologyDeleteNamespace,
  ontologyGetNamespace,
  ontologyGetNamespaces,
  ontologyUpdateNamespace,
} from "../sdk";
import type {
  CreateNamespaceRequest,
  UpdateNamespaceRequest,
  NamespaceResponse,
} from "../sdk/models";

/**
 * Service class for namespace management operations
 * Provides higher-level abstractions over the generated SDK
 */
export class NamespaceService {
  /**
   * Create a new namespace
   * @param orgId - Organization ID
   * @param name - Namespace name
   * @param properties - Optional namespace properties
   * @returns Promise with the created namespace
   */
  static async createNamespace(
    orgId: string,
    name: string,
    properties?: Record<string, unknown>
  ): Promise<NamespaceResponse> {
    const request: CreateNamespaceRequest = {
      name,
      org_id: orgId,
      properties: properties || {},
    };

    const response = await ontologyCreateNamespace({
      body: request,
    });
    return response.data;
  }

  /**
   * Get all namespaces for an organization
   * @param orgId - Organization ID
   * @returns Promise with array of namespaces
   */
  static async getNamespaces(orgId: string): Promise<NamespaceResponse[]> {
    const response = await ontologyGetNamespaces({
      query: { org_id: orgId },
    });
    return response.data || [];
  }

  /**
   * Get a specific namespace by ID
   * @param namespaceId - Namespace ID
   * @param orgId - Organization ID
   * @returns Promise with the namespace
   */
  static async getNamespace(
    namespaceId: string,
    orgId: string
  ): Promise<NamespaceResponse> {
    const response = await ontologyGetNamespace({
      path: { namespace_id: namespaceId },
      query: { org_id: orgId },
    });
    return response.data;
  }

  /**
   * Update an existing namespace
   * @param namespaceId - Namespace ID
   * @param orgId - Organization ID
   * @param name - Updated namespace name
   * @param properties - Updated namespace properties
   * @returns Promise with the updated namespace
   */
  static async updateNamespace(
    namespaceId: string,
    orgId: string,
    name?: string,
    properties?: Record<string, unknown>
  ): Promise<NamespaceResponse> {
    const request: UpdateNamespaceRequest = {
      ...(name && { name }),
      ...(properties && { properties }),
    };

    const response = await ontologyUpdateNamespace({
      path: { namespace_id: namespaceId },
      query: { org_id: orgId },
      body: request,
    });
    return response.data;
  }

  /**
   * Delete a namespace
   * @param namespaceId - Namespace ID
   * @param orgId - Organization ID
   * @returns Promise that resolves when namespace is deleted
   */
  static async deleteNamespace(
    namespaceId: string,
    orgId: string
  ): Promise<void> {
    await ontologyDeleteNamespace({
      path: { namespace_id: namespaceId },
      query: { org_id: orgId },
    });
  }

  /**
   * Get namespaces formatted for dropdown/select components
   * @param orgId - Organization ID
   * @returns Promise with array of dropdown options
   */
  static async getNamespaceOptions(orgId: string): Promise<
    Array<{
      value: string;
      label: string;
      tableCount?: number;
    }>
  > {
    const namespaces = await this.getNamespaces(orgId);

    return namespaces.map((namespace) => ({
      value: namespace.name,
      label: namespace.name,
      tableCount: namespace.table_count || 0,
    }));
  }
}
